import React, { useState, useRef } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import axios from "axios";
import { useFormik } from "formik";
import * as Yup from "yup";
import { BASE_URL, security_key } from "../../../global";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Logo from "../../../Assets/img/the-paperboy-logo-5.png"
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import LinearProgress from "@mui/material/LinearProgress";

const SignUpForm = () => {
    const navigate = useNavigate();
    const formRef = useRef(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showNavBar, setShowNavbar] = useState(true);

    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const referral_code = queryParams.get("ref");


    const togglePasswordVisibility = () => {
        setShowPassword(!showPassword);
    };

    const initialValues = {
        first_name: "",
        last_name: "",
        user_email: "",
        password: "",
        phone: "",
        address_line1: "",
        address_line2: "",
        country: "",
        state: "",
        city: "",
        zip_code: "",
        created_by: "",
        user_status: "1",
        user_level: "7",
        user_type: "3",
        referral_code: referral_code || "",

    };

    const basicSchema = Yup.object().shape({

        user_name: Yup.string()
            .required("Username is required.")
            .test("no-leading-space", "No spaces allowed at the beginning.", function (value) {
                if (!value) return true;
                return !/^\s/.test(value);
            })
            .test("starts-with-letter", "Username must start with a letter.", function (value) {
                if (!value) return true; // Skip validation if value is empty
                return /^[a-zA-Z]/.test(value);
            })
            .test("valid-characters", "Only letters, numbers, and underscores allowed.", function (value) {
                if (!value) return true; // Skip validation if value is empty
                return /^[a-zA-Z0-9_]*$/.test(value);
            })
            .test("length", "Username must be 3-20 characters long.", function (value) {
                if (!value) return true; // Skip validation if value is empty
                return value.length >= 3 && value.length <= 20;
            })
            // .matches(
            //     /^[a-zA-Z][a-zA-Z0-9_]{2,19}$/,
            //     "Username must be 3-20 characters long, start with a letter, and can only contain letters, numbers, and underscores."
            // )
            // .matches(/^[a-zA-Z][a-zA-Z0-9_]{2,19}$/, [
            //     "Username must be 3-20 characters long.",
            //     "Username must start with a letter.",
            //     "Username can only contain letters, numbers, and underscores.",
            // ])
            .test("no-trailing-space", "No spaces allowed at the end.", function (value) {
                if (!value) return true;
                return !/\s$/.test(value);
            })
            .matches(/^(?!.*__)/, "Username cannot contain consecutive underscores.")
            .test("unique-link", "This username is already taken. Please choose another one.", async function (value) {
                if (!value) return true;
                const isUserNameUnique = await checkUserName(value);
                return isUserNameUnique;
            }),

        first_name: Yup.string()
            .required("First Name is required.")
            .test("no-leading-space", "No spaces allowed at the beginning.", function (value) {
                if (!value) return true;
                return !/^\s/.test(value);
            })
            .min(3, "Minimum 3 characters.")
            .max(50, "Maximum 50 characters.")
            .test("no-trailing-space", "No spaces allowed at the end.", function (value) {
                if (!value) return true;
                return !/\s$/.test(value);
            })
            .matches(/^[a-zA-Z]*$/, "First Name can only contain letters."),

        last_name: Yup.string()
            .required("Last Name is required.")
            .test("no-leading-space", "No spaces allowed at the beginning.", function (value) {
                if (!value) return true;
                return !/^\s/.test(value);
            })
            .min(1, "Minimum 1 characters.")
            .max(250, "Maximum 50 characters.")
            .test("no-trailing-space", "No spaces allowed at the end.", function (value) {
                if (!value) return true;
                return !/\s$/.test(value);
            })
            .matches(/^[a-zA-Z]*$/, "Last Name can only contain letters."),



        address_line1: Yup.string().max(100, "Address Line 1 must be at most 100 characters.").required("Address Line 1 is required."),

        address_line2: Yup.string().max(100, "Address Line 2 must be at most 100 characters."),

        country: Yup.string().max(50, "Country must be at most 50 characters.").required("Country is required."),

        city: Yup.string().max(50, "City must be at most 50 characters.").required("City is required."),

        state: Yup.string().max(50, "State must be at most 50 characters.").required("State is required."),

        zip_code: Yup.string()
            .matches(/^\d{5}(-\d{4})?$/, "Zipcode must be a 5-digit number or a 5+4 format (e.g., 12345 or 12345-6789).")
            .required("Zipcode is required."),

        user_email: Yup.string()
            .email("Invalid email address.")
            .required("email is required.")
            .test("unique-link", "User Email is already existing in the database", async function (value) {
                if (!value) return true;
                const isUserEmailUnique = await checkUserEmail(value);
                return isUserEmailUnique;
            }),
        password: Yup.string()
            .min(8, "Password must be at least 8 characters.")
            .max(100, "Password must be at most 100 characters.")
            .matches(/[a-zA-Z]/, "Password must contain at least one letter.")
            .matches(/\d/, "Password must contain at least one number.")
            .matches(/[@$!%*?&#]/, "Password must contain at least one special character.")
            .required("Password is required."),


        phone: Yup.string()
            .required("Phone number is required.")
            .min(11, "Phone number must be at least 10 digits.")
            .max(16, "Phone number must not exceed 15 digits.")
            .test("unique-digits", "Invalid phone number.", function (value) {
                const cleanedDigits = value.replace(/^\+\D*/, "");
                const digitsWithoutCountryCode = cleanedDigits.slice(1);
                const hasDifferentDigit = Array.from(new Set(digitsWithoutCountryCode)).length !== 1;
                return hasDifferentDigit || this.createError({ message: "Invalid phone number." });
            }),
    });

    async function checkUserName(user_name) {
        const response = await axios.post(BASE_URL + "API/apicall", {
            security_key: security_key,
            mode: "checkUserName",
            user_name: user_name,
        });

        const output = response.data;

        return output == 1 ? false : true;
    }

    async function checkUserEmail(user_email) {
        const response = await axios.post(BASE_URL + "API/apicall", {
            security_key: security_key,
            mode: "checkUserEmail",
            user_email: user_email,
        });

        const output = response.data;

        return output == 1 ? false : true;
    }


    const formik = useFormik({
        initialValues,
        validationSchema: basicSchema,
        enableReinitialize: true,
        onSubmit: async (formValues) => {
            toast.dismiss();

            setIsSubmitting(true);

            const response = await axios.post(
                BASE_URL + "API/apicall",
                {
                    security_key: security_key,
                    mode: "SignUpFormData",
                    ...formValues,
                },
                {
                    headers: {
                        "Content-Type": "multipart/form-data",
                    },
                }
            );

            const output = response.data;
            if (output == 1) {
                toast.success("Added successfully!");
                setTimeout(() => navigate("/login"), 3000);
            } else {
                toast.error("Oops! Something went wrong.", {
                    autoClose: 3000,
                });
            }
            setTimeout(function () {
                setIsSubmitting(false);
            }, 3000);
        },
    });

    const { values, handleChange, handleBlur, touched, errors, handleSubmit, setFieldValue, resetForm, setValues, setTouched } = formik;

    return (
        <>
            <div className="text-center my-4">
                <Link to="/">
                    <img
                        src={Logo}
                        alt="Paperboy Logo"
                        className="img-fluid"
                        style={{ maxWidth: "200px", height: "auto" }}
                    />
                </Link>
            </div>
            <section className="pb-3">
                <ToastContainer position="top-center" />
                <div className="main-content">
                    <div className="">
                        <form onSubmit={handleSubmit} ref={formRef}>
                            <div className="card mb-2">
                                <div className="card-body">
                                    <h5>General Information</h5>
                                    <div className="row align-items-center">
                                        <div className="col-md-6">
                                            <div className="form-group mb-2">
                                                <label className="form-label mb-1">
                                                    First Name <span className="text-danger">*</span>
                                                </label>
                                                <input
                                                    type="text"
                                                    className="form-control bg-7"
                                                    name="first_name"
                                                    value={values.first_name}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    placeholder="Enter first name"
                                                />
                                                {errors.first_name && touched.first_name && (
                                                    <p className="error-msg text-danger">{errors.first_name}</p>
                                                )}
                                            </div>
                                        </div>

                                        <div className="col-md-6">
                                            <div className="form-group mb-2">
                                                <label className="form-label mb-1">
                                                    Last Name <span className="text-danger">*</span>
                                                </label>
                                                <input
                                                    type="text"
                                                    className="form-control bg-7"
                                                    name="last_name"
                                                    value={values.last_name}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    placeholder="Enter last name"
                                                />
                                                {errors.last_name && touched.last_name && (
                                                    <p className="error-msg text-danger">{errors.last_name}</p>
                                                )}
                                            </div>
                                        </div>

                                        <div className="col-md-6">
                                            <div className="form-group mb-2">
                                                <label>
                                                    User Name <span className="text-danger">*</span>
                                                </label>
                                                <div className="form-grp">
                                                    <input
                                                        type="text"
                                                        className="form-control bg-7"
                                                        name="user_name"
                                                        value={values.user_name}
                                                        onChange={handleChange}
                                                        onBlur={handleBlur}
                                                        placeholder="Enter user name"
                                                    />
                                                    {errors.user_name && touched.user_name && <p className="error-msg text-danger">{errors.user_name}</p>}
                                                </div>
                                            </div>
                                        </div>


                                        {/* <div className="col-md-6">
                                            <div className="form-group mb-2">
                                                <label>
                                                    Password <span className="text-danger">*</span>
                                                </label>
                                                <div className="form-grp">
                                                    <input
                                                        type={showPassword ? "text" : "password"}
                                                        className="form-control bg-7"
                                                        name="password"
                                                        value={values.password}
                                                        onChange={handleChange}
                                                        onBlur={handleBlur}
                                                        autoComplete="new-password"
                                                        placeholder="Enter password"
                                                        style={{ paddingRight: "2.5rem" }}

                                                    />
                                                
                                                    <div className="position-absolute" style={{ bottom: "8px", right: "12px" }}>
                                                        <button
                                                            type="button"
                                                            onClick={togglePasswordVisibility}
                                                            className="password-toggle"
                                                            style={{
                                                                background: "none",
                                                                border: "none",
                                                                cursor: "pointer",
                                                                marginRight: "730px"
                                                            }} 
                                                            dangerouslySetInnerHTML={{
                                                                __html: showPassword
                                                                    ? `<i class="fa-regular fa-eye-slash"></i>`
                                                                    : `<i class="fa-regular fa-eye"></i>`,
                                                            }}
                                                        />
                                                    </div>
                                                    {errors.password && touched.password && <p className="error-msg text-danger">{errors.password}</p>}
                                                </div>
                                            </div>
                                        </div> */}
                                        <div className="col-md-6">
                                            <div className="form-group mb-2">
                                                <label>
                                                    Password <span className="text-danger">*</span>
                                                </label>
                                                <div className="password-wrapper">
                                                    <input
                                                        type={showPassword ? "text" : "password"}
                                                        className="form-control bg-7"
                                                        name="password"
                                                        value={values.password}
                                                        onChange={handleChange}
                                                        onBlur={handleBlur}
                                                        autoComplete="new-password"
                                                        placeholder="Enter password"
                                                    />
                                                    <button
                                                        type="button"
                                                        onClick={togglePasswordVisibility}
                                                        className="password-toggle-btn"
                                                        dangerouslySetInnerHTML={{
                                                            __html: showPassword
                                                                ? `<i class='fa-regular fa-eye-slash'></i>`
                                                                : `<i class='fa-regular fa-eye'></i>`,
                                                        }}
                                                    />
                                                </div>
                                                {errors.password && touched.password && (
                                                    <p className="error-msg text-danger">{errors.password}</p>
                                                )}
                                            </div>
                                        </div>


                                    </div>
                                </div>
                            </div>
                            <div className="card mt-3">
                                <div className="card-body">
                                    <div className="row">
                                        <h5>Contact Information</h5>
                                        <div className="col-md-12 col-lg-6">
                                            <div className="form-group">
                                                <label>
                                                    Email Address <span className="text-danger">*</span>
                                                </label>
                                                <div className="form-grp">
                                                    <input
                                                        type="email"
                                                        className="form-control bg-7"
                                                        name="user_email"
                                                        value={values.user_email}
                                                        onChange={handleChange}
                                                        onBlur={handleBlur}
                                                        placeholder="Enter email"
                                                    />
                                                    {errors.user_email && touched.user_email && <p className="error-msg text-danger">{errors.user_email}</p>}
                                                </div>
                                            </div>
                                            <div className="form-group">
                                                <label>
                                                    Phone Number <span className="text-danger">*</span>
                                                </label>

                                                <div className="form-grp">
                                                    <PhoneInput
                                                        // inputStyle={{ width: "100%" }}
                                                        inputClass="form-control bg-7"
                                                        id="phone"
                                                        name="phone"
                                                        countryCodeEditable={false}
                                                        country="us"
                                                        value={values.phone}
                                                        onlyCountries={["us"]}
                                                        onChange={(value, data) => {
                                                            setFieldValue("phone", value);
                                                        }}
                                                        onBlur={(e) => {
                                                            const event = {
                                                                target: {
                                                                    name: "phone",
                                                                    value: e.target.value,
                                                                },
                                                            };
                                                            handleBlur(event);
                                                            setTouched({ ...touched, phone: true });
                                                        }}
                                                    />
                                                    {errors.phone && touched.phone && <p className="error-msg text-danger">{errors.phone}</p>}
                                                </div>
                                            </div>
                                            <div className="form-group">
                                                <label>
                                                    Address Line 1 <span className="text-danger">*</span>
                                                </label>
                                                <div className="form-grp">
                                                    <input
                                                        type="text"
                                                        className="form-control bg-7"
                                                        name="address_line1"
                                                        value={values.address_line1}
                                                        onChange={handleChange}
                                                        onBlur={handleBlur}
                                                        placeholder="Enter address line 1"
                                                    />
                                                    {errors.address_line1 && touched.address_line1 && (
                                                        <p className="error-msg text-danger">{errors.address_line1}</p>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="form-group">
                                                <label>Address Line 2</label>
                                                <div className="form-grp">
                                                    <input
                                                        type="text"
                                                        className="form-control bg-7"
                                                        name="address_line2"
                                                        value={values.address_line2}
                                                        onChange={handleChange}
                                                        onBlur={handleBlur}
                                                        placeholder="Enter address line 2"
                                                    />
                                                    {errors.address_line2 && touched.address_line2 && (
                                                        <p className="error-msg text-danger">{errors.address_line2}</p>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-md-12 col-lg-6">
                                            <div className="form-group">
                                                <label>
                                                    City <span className="text-danger">*</span>
                                                </label>
                                                <div className="form-grp">
                                                    <input
                                                        type="text"
                                                        className="form-control bg-7"
                                                        name="city"
                                                        value={values.city}
                                                        onChange={handleChange}
                                                        onBlur={handleBlur}
                                                        placeholder="Enter city"
                                                    />
                                                    {errors.city && touched.city && <p className="error-msg text-danger">{errors.city}</p>}
                                                </div>
                                            </div>
                                            <div className="form-group">
                                                <label>
                                                    State <span className="text-danger">*</span>
                                                </label>
                                                <div className="form-grp">
                                                    <input
                                                        type="text"
                                                        className="form-control bg-7"
                                                        name="state"
                                                        value={values.state}
                                                        onChange={handleChange}
                                                        onBlur={handleBlur}
                                                        placeholder="Enter state"
                                                    />
                                                    {errors.state && touched.state && <p className="error-msg text-danger">{errors.state}</p>}
                                                </div>
                                            </div>
                                            <div className="form-group">
                                                <label>
                                                    Country <span className="text-danger">*</span>
                                                </label>
                                                <div className="form-grp">
                                                    <input
                                                        type="text"
                                                        className="form-control bg-7"
                                                        name="country"
                                                        value={values.country}
                                                        onChange={handleChange}
                                                        onBlur={handleBlur}
                                                        placeholder="Enter country"
                                                    />
                                                    {errors.country && touched.country && <p className="error-msg text-danger">{errors.country}</p>}
                                                </div>
                                            </div>
                                            <div className="form-group">
                                                <label>
                                                    Zip Code <span className="text-danger">*</span>
                                                </label>
                                                <div className="form-grp">
                                                    <input
                                                        type="text"
                                                        className="form-control bg-7"
                                                        name="zip_code"
                                                        value={values.zip_code}
                                                        onChange={handleChange}
                                                        onBlur={handleBlur}
                                                        placeholder="Enter zipcode"
                                                    />
                                                    {errors.zip_code && touched.zip_code && <p className="error-msg text-danger">{errors.zip_code}</p>}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="btn-user mt-2">
                                <button type="submit" className="btn text-white btn-dark rounded-3">
                                    Sign Up
                                    {isSubmitting && <LinearProgress />}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
                <style jsx>{`
  .form-group {
    display: flex;
    flex-direction: column;
  }

  .form-label {
    font-weight: 500;
    margin-bottom: 4px !important;
  }

  .form-control {
    padding: 6px 10px;
  }

  .error-msg {
    font-size: 12px;
    margin-top: 2px;
  }

  /* 👇 Invite Friends Tree Styling */
  .invite-tree {
    position: relative;
    padding-top: 40px;
    margin-top: 30px;
  }

  /* Horizontal top line */
  .tree-line {
    position: absolute;
    top: 20px;
    left: 5%;
    right: 5%;
    height: 3px;
    background-color: #ccc;
    border-radius: 2px;
  }

  .invite-section {
    display: flex;
    justify-content: space-evenly;
    align-items: flex-start;
    flex-wrap: nowrap;
    position: relative;
  }

  .invite-card {
    background-color: #b7d7a8;
    color: white;
    font-weight: 600;
    text-align: center;
    width: 100px;
    height: 80px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    border-radius: 6px;
    cursor: pointer;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
    transition: transform 0.2s ease, background-color 0.2s ease;
    position: relative;
  }

  /* Small vertical connectors from line to box */
  .invite-card::before {
    content: "";
    position: absolute;
    top: -20px;
    left: 50%;
    transform: translateX(-50%);
    width: 2px;
    height: 20px;
    background-color: #ccc;
  }

  .invite-card:hover {
    background-color: #9ccc65;
    transform: scale(1.05);
  }

.password-wrapper {
  position: relative;
  width: fit-content;  /* Keeps wrapper only as wide as the input */
  display: inline-block;
}

.password-wrapper input {
  width: 100%; /* Keeps responsive behavior inside column */
  padding-right: 2.5rem; /* Adds space for the icon */
}

.password-toggle-btn {
  position: absolute;
  right: 10px; /* Always inside the input */
  top: 50%;
  transform: translateY(-50%);
  background: transparent;
  border: none;
  cursor: pointer;
  color: #555;
  font-size: 1rem;
  padding: 0;
  line-height: 1;
}

.password-toggle-btn:hover {
  color: #000;
}

@media (max-width: 768px) {
  .password-toggle-btn {
    right: 8px;
  }
}






`}</style>


            </section>
        </>
    );
};

export default SignUpForm;
